import React from "react";
import "../styles/NIVIntro.css"; // Import the CSS file

const NIVIntro = () => {
  return (
    <div className="container">
      <section
        className="header nvs-introduction"
        aria-labelledby="nvs-introduction-heading"
      >
        <h2 id="nvs-introduction-heading">
          Introduction to Noninvasive Ventilatory Support
        </h2>
        <p>
          Noninvasive ventilatory support (NVS) helps people with weak
          respiratory muscles breathe without necessarily requiring an invasive
          airway tube.
        </p>
        <p>
          Although noninvasive ventilation (NIV) is often associated with CPAP
          and bilevel positive airway pressure (BiPAP), people with respiratory
          muscle weakness may require a different approach to achieve adequate
          ventilation.
        </p>
        <p>
          NVS can provide breathing assistance through a nasal interface,
          mouthpiece, or oronasal mask. When combined with mechanical
          insufflation-exsufflation (MIE), it can help address both inadequate
          breathing and ineffective coughing.
        </p>
      </section>

      <section
        className="section respiratory-problem"
        aria-labelledby="respiratory-problem-heading"
      >
        <h2 id="respiratory-problem-heading">
          The Problem: When Respiratory Muscle Weakness Is Mismanaged
        </h2>
        <div className="respiratory-problem-grid">
          <article>
            <h3>When breathing and coughing become too weak</h3>
            <p>
              People with severe respiratory muscle weakness may struggle both
              to breathe deeply enough and to cough effectively. When cough
              strength declines, mucus can accumulate in the airways, increasing
              the risk of respiratory infections and pneumonia. As breathing
              muscles weaken, inadequate ventilation can also cause carbon
              dioxide (CO₂) to build up in the blood.
            </p>
          </article>
          <article>
            <h3>When a respiratory crisis occurs</h3>
            <p>
              During an emergency, patients may receive oxygen to address low
              blood oxygen levels. However, oxygen alone does not correct
              inadequate ventilation and may worsen carbon dioxide retention
              in some vulnerable patients. Without appropriate assessment and
              ventilatory support, respiratory failure can progress, potentially
              leading to reduced consciousness, emergency intubation, and
              invasive mechanical ventilation.
            </p>
          </article>
          <article>
            <h3>When temporary ventilation becomes long-term dependence</h3>
            <p>
              For some patients, removing an endotracheal tube and returning to
              independent breathing can be difficult. Families may then face
              complex decisions about continued invasive ventilation,
              tracheostomy, or comfort-focused care. These decisions can be
              especially challenging when families are unaware of other
              respiratory support strategies that might be appropriate.
            </p>
          </article>
          <article className="cough-support">
            <h3>Supporting an Effective Cough</h3>
            <p>
              Weak cough muscles can make it difficult to clear mucus from the
              airways, which may increase the risk of respiratory infections
              and pneumonia. Mechanical insufflation-exsufflation (MIE), also
              called mechanical cough assistance, helps move air into and then
              out of the lungs to support cough and secretion clearance.
            </p>
            <p>
              Oxygen may be needed to treat low blood oxygen, but it does not
              clear airway mucus or correct inadequate ventilation on its own.
              For people with respiratory muscle weakness, clinicians can
              assess whether ventilatory support and cough-assistance
              techniques such as MIE are appropriate as part of an
              individualized care plan.
            </p>
          </article>
        </div>
      </section>

      <section className="section principles-section">
        <h3>Principles That Your Doctor is Very Unlikely to Have Heard of</h3>
        <ul className="list-inside">
          <li>
            No one needs a tracheotomy tube for only being too weak to breathe.
          </li>
          <li>
            “NIV” (rather than “NVS”) is inadequate for long-term continuous
            noninvasive ventilatory support (CNVS) unless used at NVS settings,
            which it rarely is.
          </li>
          <li>
            Patients are left to go into acute respiratory failure and undergo
            tracheotomies although it was known that this could be avoided by
            CNVS and MIE since 1953. People with weak muscles continue to be
            extubated to oxygen, CPAP, and low span bi-level PAP, and when
            failing extubation as a result, they are re-intubated and trached or
            undergo palliative care deaths despite publications reporting the
            extubations of ventilator unweanable people since 1990. NVS and
            mechanical insufflation exsufflation (MIE) that makes cough flows
            effective, prevents pneumonias and respiratory failure but this
            continues to be ignored despite MIE being available since 1953.
          </li>
          <li>
            No one should be treating neuromuscular diseases or weak respiratory
            muscles without knowledge of NVS and MIE yet only the Rutgers Health
            MDA clinic in Newark, New Jersey, and extremely few other centers in
            the U.S., use NVS and MIE to definitively prevent resort to
            tracheostomies for people with only severe muscle weakness despite
            the fact that there are many other centers that do so in South
            America, Korea, Japan, Canada, and Mexico. See the icon for Centers.
          </li>
          <li>
            Since routine polysomnograms do not distinguish shallow breaths from
            central and obstructive causes, from inspiratory muscle weakness,
            people with weak respiratory muscles are futilely treated with NIV
            instead of the NVS and MIE they need to prevent respiratory failure
            and invasive airway tubes.
          </li>
          <li>
            Invasive airway tubes hinder or prevent weaning from ventilator use
            but when they are removed, even from people with no ability to
            breathe on their own, people then wean back to the ventilator use,
            or even no ventilator use, they were doing before ever going to the
            hospital.
          </li>
          <li>
            Even ventilator unweanable infants can be extubated to NVS and MIE
            without resort to tracheostomy tubes and safely discharged home
            despite NVS dependence.
          </li>
          <li>
            Sophisticated ventilators are not necessary for NVS. The only
            important alarm is oximetry on the finger for blood O2 saturation.
          </li>
          <li>
            Removing the tracheotomy tubes of people too weak to breathe to NVS
            use (decannulation) can permit them to wean to part-time or even no
            ventilator use. Airway tubes hinder or prevent weaning.
          </li>
          <li>
            “Pulmonary function studies,” polysomnographies, intubations,
            bronchoscopies, tracheotomies, and ongoing nursing care for invasive
            airway tubes can be avoided entirely for people only too weak to
            breathe but all of these invasive procedures are well remunerated,
            keeping people healthy with CNVS and MIE is not.
          </li>
          <li>
            The home administration of O2 instead of NVS and MIE can have
            catastrophic, indeed deadly consequences for people with weak
            respiratory muscles.
          </li>
          <li>
            While there are many lessons to be learned, few are, when the effort
            to learn them and shift a treatment paradigm is not buoyed by
            financial gain.
          </li>
          <li>
            The EPAP of bipap increases the risk of aspiration, reflux, cardiac
            arrhythmias, disrupts sleep, decreases venous return, gushes air,
            and prevents full exhalation, and is both unnecessary,
            uncomfortable, and counterproductive for people with healthy lungs.
          </li>
          <li>
            People lose 1 to 1.2% of their ability to take deep breaths per year
            (vital capacity) from age 20 so when very old or weak for any
            reasons they are often only offered death via O2 and morphine or
            tracheostomy tubes through their necks. It must be emphasized that
            NVS and MIE can be alternatives to this.
          </li>
        </ul>
      </section>
      <section className="section benefit-section">
        <h3>Who May Benefit from NVS?</h3>
        <p className="benefit-intro">
          NVS may be considered when a condition affects breathing, especially
          when respiratory muscle weakness contributes to inadequate
          ventilation. Depending on an individual assessment, this may include
          people with neuromuscular conditions, chronic obstructive pulmonary
          disease (COPD), high spinal cord injury, or severe obesity, as well as
          some older adults.
        </p>
        <p className="benefit-guidance">
          The need for NVS and the appropriate approach vary from person to
          person. A qualified healthcare professional can assess breathing,
          cough strength, and other factors to determine suitable support.
        </p>
        <h4>Examples of conditions managed with NVS</h4>
        <ul className="list-inside benefit-cards">
          <li>
            <strong>Duchenne muscular dystrophy (DMD)</strong>
            <span>
              Progressive respiratory muscle weakness can contribute to
              nighttime hypoventilation.
            </span>
          </li>
          <li>
            <strong>Amyotrophic lateral sclerosis (ALS)</strong>
            <span>
              Respiratory support may help address breathing difficulties as
              muscle weakness progresses.
            </span>
          </li>
          <li>
            <strong>Spinal muscular atrophy (SMA)</strong>
            <span>
              NVS may be part of respiratory care, including for some people
              with SMA types 1 and 2.
            </span>
          </li>
        </ul>
      </section>

      {/* Image between the sections 

      <section className="section mt-8">
        <h3>Benefits of NVS</h3>
        <ul className="list-inside">
          <li>
            <strong>Delays or Avoids Invasive Ventilation</strong>: NVS helps
            patients avoid the need for mechanical ventilation, which carries
            risks like infection.
          </li>
          <li>
            <strong>Improves Gas Exchange</strong>: By improving oxygen levels
            and decreasing carbon dioxide retention, NVS enhances overall
            respiratory function.
          </li>
          <li>
            <strong>Reduces Respiratory Muscle Workload</strong>: It eases the
            burden on weakened respiratory muscles, especially in neuromuscular
            diseases.
          </li>
          <li>
            <strong>Improves Sleep and Daytime Energy</strong>: NVS improves
            sleep quality, reducing symptoms like morning headaches, fatigue,
            and poor concentration.
          </li>
          <li>
            <strong>Increases Patient Independence</strong>: NVS allows for more
            independence, allowing patients to continue living at home and
            engage in daily activities with less disruption from respiratory
            issues.
          </li>
        </ul>
      </section>*/}
    </div>
  );
};

export default NIVIntro;
